import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { TeamMember, TeamInvitation, InviteTeamMemberRequest, TeamRole } from '../types';
import toast from 'react-hot-toast';

// Get team members and invitations
export const useTeamMembers = () => {
    return useQuery({
        queryKey: ['team', 'members'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: {
                    members: TeamMember[];
                    pending_invitations: TeamInvitation[];
                }
            }>('/team');
            return data.data.members;
        },
    });
};

// Get team invitations
export const useTeamInvitations = () => {
    return useQuery({
        queryKey: ['team', 'invitations'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: {
                    members: TeamMember[];
                    pending_invitations: TeamInvitation[];
                }
            }>('/team');
            return data.data.pending_invitations;
        },
    });
};

// Invite team member
export const useInviteTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (inviteData: InviteTeamMemberRequest) => {
            const { data } = await api.post<{ success: boolean; data: TeamInvitation; message: string }>(
                '/team/invite',
                inviteData
            );
            return data.data;
        },
        onSuccess: () => {
            // Invalidate both members and invitations queries
            queryClient.invalidateQueries({ queryKey: ['team'] });
            toast.success('Invitation sent successfully');
        },
        onError: (error: any) => {
            const errorCode = error.response?.data?.error?.code;
            const errorMessage = error.response?.data?.error?.message || 'Failed to send invitation';

            // Special handling for team limit reached
            if (errorCode === 'TEAM_LIMIT_REACHED') {
                toast.error(`Team Limit Reached: ${errorMessage}`, {
                    duration: 5000,
                });
            } else {
                toast.error(errorMessage);
            }
        },
    });
};

// Cancel invitation
export const useCancelInvitation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (invitationId: number) => {
            await api.delete(`/team/invitations/${invitationId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            toast.success('Invitation cancelled');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message || 'Failed to cancel invitation';
            toast.error(errorMessage);
        },
    });
};

// Remove team member
export const useRemoveTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (memberId: number) => {
            await api.delete(`/team/members/${memberId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            toast.success('Member removed successfully');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message || 'Failed to remove member';
            toast.error(errorMessage);
        },
    });
};

// Update member role
export const useUpdateMemberRole = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ memberId, role }: { memberId: number; role: TeamRole }) => {
            const { data } = await api.put(`/team/members/${memberId}/role`, { role });
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['team'] });
            toast.success('Role updated successfully');
        },
        onError: (error: any) => {
            const errorMessage = error.response?.data?.error?.message || 'Failed to update role';
            toast.error(errorMessage);
        },
    });
};
